// App.js - Corrected version
import React, { useState, useEffect } from 'react';
// import PrivateAccessGate from './components/PrivateAccessGate';
import ProfileSetup from './components/ProfileSetup';
import ModalContainer from './components/ModalContainer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MediaDataProvider, useMediaDataContext } from './contexts/MediaDataContext';
import { ModalProvider, useModal } from './contexts/ModalContext';
import { FormProvider, useForm } from './contexts/FormContext';
import { SearchProvider, useSearch } from './contexts/SearchContext';
import { CommunityProvider, useCommunity } from './contexts/CommunityContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import { PageLoading } from './components/LoadingStates';
import NotificationContainer from './components/NotificationContainer';
import { useProfile } from './hooks/useProfile';
import AuthModal from './components/AuthModal';
import AddMediaModal from './components/AddMediaModal';
import AddToCollectionModal from './components/AddToCollectionModal';
import UnifiedAddModal from './components/UnifiedAddModal';
import ImageLightbox from './components/ImageLightbox';
import HeaderNav from './components/HeaderNav';
import Footer from './components/Footer';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { useMovieAddForm } from './components/MovieAddForm';
import { useCommunityHandler } from './components/CommunityHandler';
import { useWantlistHandlers } from './hooks/useWantlistHandlers';
import { useCollectionHandlers } from './hooks/useCollectionHandlers';
import { setupWindowFunctions } from './utils/windowFunctions';
import { AppRoutes } from './components/AppRoutes';
import ScrollToTop from './components/ScrollToTop';
import ProfileCompletionWatcher from './components/ProfileCompletionWatcher';
import { supabase } from './lib/supabase';

// Themed wrapper component
const ThemedApp = () => {
  return <VHSCatalog />;
};

const VHSCatalog = () => {
  const navigate = useNavigate();

  // Import useAuth from context instead of hook
  const { currentUser, isAdmin, loading } = useAuth();

  // Initialize modal context
  const modal = useModal();

  // Initialize form context
  const form = useForm();

  // Initialize search context
  const search = useSearch();

  // Initialize community context
  const community = useCommunity();

  // Initialize other hooks
  const profile = useProfile(supabase, currentUser);

  // Get all VHS data from context instead of hook
  const vhsData = useMediaDataContext();

  // Destructure what we need from hooks
  const { userProfile, showProfileSetup, handleProfileCreated } = profile;
  const {
    masterItems,
    allReleases,
    userCollectionItems,
    marketplaceListings,
    userWantlist,
    newMasterItem,
    setNewMasterItem,
    newCollectionItem,
    setNewCollectionItem,
    handleEditMasterItem,
    handleDeleteMasterItem,
    removeFromCollection,
    addMarketplaceListing,
    removeMarketplaceListing,
    removeFromCollectionAndMarketplace,
    resetForms,
    loadUserCollection,
    isItemListed, // ← NEW
    getSellerRating, // ← NEW
    loadActivityFeed, // ← NEW
    loadUserWantlist, // ← NEW
    addReleaseToWantlist, // ← NEW
    removeReleaseFromWantlist, // ← NEW
    isReleaseWanted, // ← NEW
    hasWantedReleases, // ← NEW
  } = vhsData;

  // Local state variables
  const [, setUploadingImage] = useState(false);

  // Calculate count of wantlist items that are available for sale
  const wantlistItemsForSaleCount = userWantlist.filter(wantlistItem => {
    const release = wantlistItem.movie_releases;
    const movie = release?.media_titles;
    if (!release || !movie) return false;

    // Check if there are any marketplace listings for this specific release OR other releases of the same movie
    return marketplaceListings.some(
      listing =>
        listing.release_id === release.id ||
        (listing.master_movie_id === movie.id && listing.release_id !== release.id)
    );
  }).length;
  const [unreadMarketplaceCount, setUnreadMarketplaceCount] = useState(0);
  const [unreadCommunityCount, setUnreadCommunityCount] = useState(0);

  // Function to load unread community message count
  const loadUnreadCommunityCount = async () => {
    if (!supabase || !currentUser) return;

    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', currentUser.id)
        .eq('is_read', false);

      if (error) throw error;
      setUnreadCommunityCount(count || 0);
    } catch (err) {
      console.error('Error loading unread community count:', err);
    }
  };

  // Load unread community message count when user is available
  useEffect(() => {
    loadUnreadCommunityCount();
  }, [supabase, currentUser]);
  const [availableReleases, setAvailableReleases] = useState([]);

  const resetFormsWithImages = () => {
    resetForms(); // Call the hook's resetForms
    form.resetFormsWithImages(); // Use FormContext function
  };

  // Initialize movie form handlers
  const movieFormHandlers = useMovieAddForm(
    supabase,
    currentUser,
    userProfile,
    setUploadingImage,
    setNewMasterItem,
    newMasterItem,
    newCollectionItem,
    loadUserCollection
  );

  // Initialize community handlers
  const communityHandlers = useCommunityHandler(supabase, currentUser, userProfile);

  // Load community data on mount (for all users including anonymous)
  useEffect(() => {
    communityHandlers.loadCommunityData();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Define functions needed by setupWindowFunctions
  const resetReleaseForm = () => {
    form.resetReleaseForm();
    form.setSelectedReleaseImages([]);
  };

  // Setup all window functions (only when core dependencies change)
  useEffect(() => {
    setupWindowFunctions({
      navigate,
      modal,
      form,
      resetFormsWithImages,
      resetReleaseForm,
      vhsData,
      removeFromCollection,
      removeFromCollectionAndMarketplace,
      handleDeleteMasterItem,
      handleEditMasterItem,
      setAvailableReleases,
      loadUserCollection,
      loadActivityFeed,
      supabase,
      setNewCollectionItem,
    });
    // Only re-run when user changes or core contexts change
  }, [currentUser, supabase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Movie form handlers
  const { handleMovieSearchSelect, handleSaveMovieAndRelease, handleUPCScanComplete } =
    movieFormHandlers;

  // Community handlers
  const { handleNewForumPost, handleNewTradeRequest } = communityHandlers;

  // Wantlist handlers
  const wantlistHandlers = useWantlistHandlers({
    hasWantedReleases,
    userWantlist,
    addReleaseToWantlist,
    loadUserWantlist,
  });

  // Collection handlers
  const collectionHandlers = useCollectionHandlers({
    userCollectionItems,
    addToCollectionWithRelease: vhsData.addToCollectionWithRelease,
    removeFromCollection,
    loadUserCollection,
  });

  const { handleWantlistModalClick, handleCloseWantedReleasesModal } = wantlistHandlers;

  const { handleCollectionModalClick } = collectionHandlers;

  if (loading) {
    return <PageLoading message="Building catalog..." />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Profile Completion Watcher */}
      <ProfileCompletionWatcher />

      {/* Header Navigation */}
      <HeaderNav
        masterItems={masterItems}
        userCollectionItems={userCollectionItems}
        wantlistItemsForSaleCount={wantlistItemsForSaleCount}
        unreadMarketplaceCount={unreadMarketplaceCount}
        unreadCommunityCount={unreadCommunityCount}
        userProfile={userProfile}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* All your existing modals stay here */}
        {/* Auth Modal */}
        <AuthModal />

        {/* Poster Modal */}
        <ImageLightbox
          isOpen={modal.isModalOpen('imageModal')}
          onClose={() => modal.closeModal('imageModal')}
          imageUrl={modal.getModalProps('imageModal').imageUrl || ''}
          title={modal.getModalProps('imageModal').title || ''}
          allowZoom={true}
        />
        <AddMediaModal
          isOpen={modal.isModalOpen('addMaster')}
          onClose={() => {
            modal.closeModal('addMaster');
            form.setEditingMasterItem(null);
            form.setSelectedMasterImages([]);
            form.setSelectedReleaseImages([]);
            resetFormsWithImages();
            resetReleaseForm();
            form.setAddToMyCollection(true);
            form.setAddToMyWantlist(false);
          }}
          editingMasterItem={form.editingMasterItem}
          selectedMasterImages={form.selectedMasterImages}
          setSelectedMasterImages={form.setSelectedMasterImages}
          selectedReleaseImages={form.selectedReleaseImages}
          setSelectedReleaseImages={form.setSelectedReleaseImages}
          addToMyCollection={form.addToMyCollection}
          setAddToMyCollection={form.setAddToMyCollection}
          addToMyWantlist={form.addToMyWantlist}
          setAddToMyWantlist={form.setAddToMyWantlist}
          releaseFormData={form.releaseForm}
          setReleaseFormData={form.setReleaseForm}
          showReleaseImageUpload={modal.isModalOpen('releaseImageUpload')}
          setShowReleaseImageUpload={show =>
            show ? modal.openModal('releaseImageUpload') : modal.closeModal('releaseImageUpload')
          }
          // Search/scan functions
          setShowMovieSearch={show =>
            show ? modal.openModal('movieSearch') : modal.closeModal('movieSearch')
          }
          setShowUPCScanner={show =>
            show ? modal.openModal('upcScanner') : modal.closeModal('upcScanner')
          }
          isDataFromSearch={form.isDataFromSearch}
          // Reset functions
          resetFormsWithImages={resetFormsWithImages}
          resetReleaseForm={resetReleaseForm}
          // Save function
          handleSaveMovieAndRelease={handleSaveMovieAndRelease}
          // Admin privileges
          isAdmin={isAdmin}
        />

        <AddToCollectionModal
          isOpen={modal.isModalOpen('addToCollection')}
          onClose={() => {
            modal.closeModal('addToCollection');
            form.setSelectedMasterItem(null);
            setAvailableReleases([]);
            setNewCollectionItem({ personal_notes: '' });
          }}
          selectedMasterItem={
            form.selectedMasterItem || modal.getModalProps('addToCollection').masterItem
          }
          availableReleases={availableReleases}
          onSuccess={() => {
            // Reload collection data after successful addition
            vhsData.loadUserCollection();
          }}
          // Pass unified mode props
          mode={modal.getModalProps('addToCollection').mode}
          userCollectionItems={
            modal.getModalProps('addToCollection').userCollectionItems || userCollectionItems
          }
          currentUser={currentUser}
          supabase={supabase}
        />

        {/* Unified Collection Modal */}
        <UnifiedAddModal
          isOpen={
            modal.isModalOpen('addToCollection') &&
            modal.getModalProps('addToCollection').mode === 'unified'
          }
          onClose={() => modal.closeModal('addToCollection')}
          movie={modal.getModalProps('addToCollection').movie}
          mode="collection"
          userCollectionItems={userCollectionItems}
          supabase={supabase}
          currentUser={currentUser}
          onSuccess={() => {
            // Reload collection data after successful addition/removal
            vhsData.loadUserCollection();
          }}
        />

        {/* Unified Wantlist Modal - using same component with mode="wantlist" */}
        <UnifiedAddModal
          isOpen={
            modal.isModalOpen('wantlist') && modal.getModalProps('wantlist').mode === 'unified'
          }
          onClose={() => modal.closeModal('wantlist')}
          movie={modal.getModalProps('wantlist').movie}
          mode="wantlist"
          userCollectionItems={userCollectionItems}
          userWantlist={userWantlist}
          supabase={supabase}
          currentUser={currentUser}
          addReleaseToWantlist={addReleaseToWantlist}
          removeReleaseFromWantlist={removeReleaseFromWantlist}
          onSuccess={() => {
            // Reload wantlist data after successful addition/removal
            vhsData.loadUserWantlist();
          }}
        />

        {/* Other modals - Profile Setup, Movie Search, UPC Scanner, etc. */}
        {showProfileSetup && currentUser && (
          <ProfileSetup
            currentUser={currentUser}
            supabase={supabase}
            onProfileCreated={handleProfileCreated}
          />
        )}

        {/* All other modals handled by ModalContainer */}
        <ModalContainer
          supabase={supabase}
          supabaseUrl={process.env.REACT_APP_SUPABASE_URL}
          supabaseAnonKey={process.env.REACT_APP_SUPABASE_ANON_KEY}
          handleMovieSearchSelect={handleMovieSearchSelect}
          handleUPCScanComplete={handleUPCScanComplete}
          handleCloseWantedReleasesModal={handleCloseWantedReleasesModal}
          userProfile={userProfile}
        />

        {/* Routes */}
        <AppRoutes
          // User and auth
          currentUser={currentUser}
          isAdmin={isAdmin}
          userProfile={userProfile}
          // Data
          masterItems={masterItems}
          allReleases={allReleases}
          userCollectionItems={userCollectionItems}
          userWantlist={userWantlist}
          marketplaceListings={marketplaceListings}
          availableReleases={availableReleases}
          // VHS Data functions
          vhsData={vhsData}
          // Modal functions
          modal={modal}
          // Form functions
          form={form}
          // Community data
          community={community}
          // Search functions
          search={search}
          // Handler functions
          handleEditMasterItem={handleEditMasterItem}
          handleDeleteMasterItem={handleDeleteMasterItem}
          handleNewForumPost={handleNewForumPost}
          handleNewTradeRequest={handleNewTradeRequest}
          handleWantlistModalClick={handleWantlistModalClick}
          handleCollectionModalClick={handleCollectionModalClick}
          // Marketplace functions
          addMarketplaceListing={addMarketplaceListing}
          removeMarketplaceListing={removeMarketplaceListing}
          isItemListed={isItemListed}
          getSellerRating={getSellerRating}
          // Wantlist functions
          addReleaseToWantlist={addReleaseToWantlist}
          removeReleaseFromWantlist={removeReleaseFromWantlist}
          isReleaseWanted={isReleaseWanted}
          hasWantedReleases={hasWantedReleases}
          addMovieToWantlist={vhsData.addMovieToWantlist}
          removeMovieFromWantlist={vhsData.removeMovieFromWantlist}
          isMovieWanted={vhsData.isMovieWanted}
          // Other props
          supabase={supabase}
          setUploadingImage={setUploadingImage}
          setUnreadMarketplaceCount={setUnreadMarketplaceCount}
          setUnreadCommunityCount={setUnreadCommunityCount}
          refreshUnreadCommunityCount={loadUnreadCommunityCount}
          unreadCommunityCount={unreadCommunityCount}
        />
      </main>

      {/* Footer */}
      <Footer />

      {/* Notification Container */}
      <NotificationContainer />
    </div>
  );
};

export default function App() {
  return (
    <ErrorBoundary level="app">
      <Router>
        <ScrollToTop />
        <NotificationProvider>
          <AuthProvider supabase={supabase}>
            <MediaDataProvider supabase={supabase}>
              <ErrorBoundary level="page">
                <ModalProvider>
                  <FormProvider>
                    <SearchProvider supabase={supabase}>
                      <CommunityProvider>
                        <ThemedApp />
                      </CommunityProvider>
                    </SearchProvider>
                  </FormProvider>
                </ModalProvider>
              </ErrorBoundary>
            </MediaDataProvider>
          </AuthProvider>
        </NotificationProvider>
      </Router>
    </ErrorBoundary>
  );
}
